import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useFiltersContext } from "src/contexts/FilterContext";
import { CheckBoxListItem, DateListItem, UserListItem } from "src/types/main";
import { FlexRow, FlexCol } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { Keys, RestKeys, DateKeys, UserKeys, EquipmentKeys, FiltersStateType } from "../../type";
import { ListEquipmentsWithSearch, ListUserWithSearch, ListWithSearch, TGListWithSearch } from "./ListsSearch";
import { CheckboxUI } from "./CheckLists";
import { DateValueType } from "react-tailwindcss-datepicker";
import TGDatePicker from "src/components/ui/TGDatePicker";

export function FilterItem({
  keyItem,
  data,
  title,
  filters,
}: {
  keyItem: Keys | RestKeys;
  title: string;
  data: (CheckBoxListItem & { disabled?: boolean })[];
  filters: FiltersStateType;
}) {
  return (
    <Disclosure as="div" className="border-b">
      {({ open, close }) => (
        <>
          <Disclosure.Button
            className={twMerge(
              open
                ? "text-black pt-5 pb-[15px] tablet:px-5"
                : filters[keyItem] && filters[keyItem].length > 0
                ? "text-black py-5 tablet:p-5"
                : "text-[#637381] py-5 tablet:p-5",

              "flex justify-between w-full text-sm font-medium text-left"
            )}
          >
            <FlexRow className="gap-x-1">
              <FlexCol>{title}</FlexCol>
              {filters[keyItem] && filters[keyItem].length > 0 ? (
                <>
                  <FlexCol className="justify-center item-center"> · </FlexCol>
                  <FlexCol>{filters[keyItem].length}</FlexCol>
                </>
              ) : (
                ""
              )}
            </FlexRow>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </Disclosure.Button>
          <Panel close={close} data={data} keyItem={keyItem} key={keyItem} />
        </>
      )}
    </Disclosure>
  );
}

export function TGFilterItem<T extends string>({
  keyItem,
  listData,
  items,
  title,
  checkedItems,
  requestFn,
  onResetFilter,
}: {
  keyItem: T;
  title: string;
  listData: (CheckBoxListItem & { disabled?: boolean })[];
  items: (CheckBoxListItem & { disabled?: boolean })[];
  checkedItems: (item: CheckBoxListItem) => void;
  onResetFilter?: (key: string) => void;
  requestFn?: (query: string) => Promise<CheckBoxListItem[]>;
}) {
  function resetFilter(key: T) {
    onResetFilter && onResetFilter(key);
  }

  return (
    <Disclosure as="div" className="border-b">
      {({ open, close }) => (
        <>
          <Disclosure.Button
            className={twMerge(
              open ? "text-black pt-5 pb-[15px] tablet:px-5" : items.length > 0 ? "text-black py-5 tablet:p-5" : "text-[#637381] py-5 tablet:p-5",
              "flex justify-between w-full text-sm font-medium text-left"
            )}
          >
            <FlexRow className="gap-x-1">
              <FlexCol>{title}</FlexCol>
              {items && items.length > 0 ? (
                <>
                  <FlexCol className="justify-center item-center"> · </FlexCol>
                  <FlexCol>{items.length}</FlexCol>
                </>
              ) : (
                ""
              )}
            </FlexRow>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </Disclosure.Button>
          <Disclosure.Panel className="flex flex-col gap-y-[15px] px-5 pb-[15px] text-sm text-gray-500">
            <TGListWithSearch listData={listData} checkedItems={checkedItems} items={items} localSearch={requestFn} />
            <FlexRow
              className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
              onClick={() => resetFilter(keyItem)}
            >
              <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
              <FlexCol className="pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_33_4954)">
                    <path
                      d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                      fill="#637381"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_33_4954">
                      <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                    </clipPath>
                  </defs>
                </svg>
              </FlexCol>
            </FlexRow>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

function Panel({
  close,
  keyItem,
  data,
}: {
  close: (focusableElement?: HTMLElement | React.MutableRefObject<HTMLElement | null> | undefined) => void;
  keyItem: Keys | RestKeys;
  data: (CheckBoxListItem & { disabled?: boolean })[];
}) {
  const { resetFilter } = useFiltersContext();

  return (
    <Disclosure.Panel className="flex flex-col gap-y-[15px] px-5 pb-[15px] text-sm text-gray-500">
      <ListWithSearch keyItem={keyItem} items={data} />
      <FlexRow
        className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
        onClick={() => resetFilter(keyItem)}
      >
        <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
        <FlexCol className="pt-1">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_33_4954)">
              <path
                d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                fill="#637381"
              />
            </g>
            <defs>
              <clipPath id="clip0_33_4954">
                <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
              </clipPath>
            </defs>
          </svg>
        </FlexCol>
      </FlexRow>
    </Disclosure.Panel>
  );
}

export function FilterDateItem({ keyItem, data, title }: { keyItem: DateKeys; data: DateListItem; title: string }) {
  const { filters, resetFilter, addFilter } = useFiltersContext();
  const [isChecked, setIsChecked] = useState(filters[keyItem].required);
  const [value, setValue] = useState<DateValueType>({
    startDate: data.date.start,
    endDate: data.date.end,
  });

  function toggleRevision() {
    const obj = {
      ...data,
      date: {
        start: value!.startDate ? new Date(value!.startDate) : null,
        end: value!.endDate ? new Date(value!.endDate) : null,
      },
      required: !isChecked,
    };

    addFilter(keyItem, obj);

    setIsChecked(!isChecked);
  }

  function changeDate(value: DateValueType) {
    const obj = {
      ...data,
      date: {
        start: value!.startDate ? new Date(value!.startDate) : null,
        end: value!.endDate ? new Date(value!.endDate) : null,
      },
      required: isChecked,
    };
    setValue(value);
    addFilter(keyItem, obj);
  }

  useEffect(() => {
    setIsChecked(filters[keyItem].required);
    setValue({
      startDate: filters[keyItem].date.start,
      endDate: filters[keyItem].date.end,
    });
  }, [filters, keyItem]);

  return (
    <Disclosure as="div" className="border-b">
      {({ open }) => (
        <>
          <Disclosure.Button
            className={twMerge(
              open
                ? "text-black pt-5 pb-[15px] tablet:px-5"
                : filters[keyItem].date.start || filters[keyItem].date.end
                ? "text-black py-5 tablet:p-5"
                : "text-[#637381] py-5 tablet:p-5",
              "flex justify-between w-full text-sm font-medium text-left"
            )}
          >
            <FlexRow className="gap-x-1">
              <FlexCol>{title}</FlexCol>
            </FlexRow>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </Disclosure.Button>
          <Disclosure.Panel className="flex flex-col gap-y-[15px] px-5 pb-[15px] text-sm text-gray-500">
            <CheckboxUI
              disabled={false}
              index={0}
              item={{ id: 0, title: "Требуется пересмотр" }}
              onChecked={toggleRevision}
              isCheck={isChecked}
            />
            <TGDatePicker value={value} onChange={changeDate} />
            <FlexRow
              className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
              onClick={() => resetFilter(keyItem)}
            >
              <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
              <FlexCol className="pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_33_4954)">
                    <path
                      d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                      fill="#637381"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_33_4954">
                      <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                    </clipPath>
                  </defs>
                </svg>
              </FlexCol>
            </FlexRow>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

export function FilterUserItem({
  keyItem,
  data,
  title,
}: {
  keyItem: UserKeys;
  title: string;
  data: (UserListItem & { disabled?: boolean })[];
}) {
  const { filters, resetFilter } = useFiltersContext();

  return (
    <Disclosure as="div" className="border-b">
      {({ open }) => (
        <>
          <Disclosure.Button
            className={twMerge(
              open
                ? "text-black pt-5 pb-[15px] tablet:px-5"
                : filters[keyItem].length > 0
                ? "text-black py-5 tablet:p-5"
                : "text-[#637381] py-5 tablet:p-5",
              "flex justify-between w-full text-sm font-medium text-left"
            )}
          >
            <FlexRow className="gap-x-1">
              <FlexCol>{title}</FlexCol>
              {filters[keyItem] && filters[keyItem].length > 0 ? (
                <>
                  <FlexCol className="justify-center item-center"> · </FlexCol>
                  <FlexCol>{filters[keyItem].length}</FlexCol>
                </>
              ) : (
                ""
              )}
            </FlexRow>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </Disclosure.Button>
          <Disclosure.Panel className="flex flex-col gap-y-[15px] px-5 pb-[15px] text-sm text-gray-500">
            <ListUserWithSearch keyItem={keyItem} items={data} />
            <FlexRow
              className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
              onClick={() => resetFilter(keyItem)}
            >
              <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
              <FlexCol className="pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_33_4954)">
                    <path
                      d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                      fill="#637381"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_33_4954">
                      <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                    </clipPath>
                  </defs>
                </svg>
              </FlexCol>
            </FlexRow>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

export function FilterEquipmentsItem({
  keyItem,
  data,
  title,
}: {
  keyItem: EquipmentKeys;
  title: string;
  data: (CheckBoxListItem & { disabled?: boolean })[];
}) {
  const { filters, resetFilter } = useFiltersContext();

  return (
    <Disclosure as="div" className="border-b">
      {({ open }) => (
        <>
          <Disclosure.Button
            className={twMerge(
              open
                ? "text-black pt-5 pb-[15px] tablet:px-5"
                : filters[keyItem].length > 0
                ? "text-black py-5 tablet:p-5"
                : "text-[#637381] py-5 tablet:p-5",
              "flex justify-between w-full text-sm font-medium text-left"
            )}
          >
            <FlexRow className="gap-x-1">
              <FlexCol>{title}</FlexCol>
              {filters[keyItem] && filters[keyItem].length > 0 ? (
                <>
                  <FlexCol className="justify-center item-center"> · </FlexCol>
                  <FlexCol>{filters[keyItem].length}</FlexCol>
                </>
              ) : (
                ""
              )}
            </FlexRow>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </Disclosure.Button>
          <Disclosure.Panel className="flex flex-col gap-y-[15px] px-5 pb-[15px] text-sm text-gray-500">
            <ListEquipmentsWithSearch keyItem={keyItem} items={data} />
            <FlexRow
              className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
              onClick={() => resetFilter(keyItem)}
            >
              <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
              <FlexCol className="pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_33_4954)">
                    <path
                      d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                      fill="#637381"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_33_4954">
                      <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                    </clipPath>
                  </defs>
                </svg>
              </FlexCol>
            </FlexRow>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
